import type {
  Agent,
  AgentNetwork,
  Approval,
  ApplyProposalResult,
  CreateModelPricing,
  ModelPricing,
  TenantSettings,
  UpdateModelPricing,
  UpdateTenantSettings,
  ApplyResult,
  CreateAgent,
  UpdateAgent,
  CreateReviewPolicy,
  CreateTaxonomyLabel,
  CreateTool,
  UpdateTool,
  CreateTrigger,
  CreateWorkspace,
  Conversation,
  EvolutionInsight,
  CreateIntegration,
  CreateKnowledgeBase,
  CreateSkill,
  Document,
  EvalCase,
  EvalResult,
  EvalSet,
  IngestDocument,
  Integration,
  IntegrationStatus,
  UpdateIntegration,
  KnowledgeBase,
  LearningSuggestion,
  McpServer,
  RetrievalQuery,
  RetrievedChunk,
  Skill,
  UpdateSkill,
  Message,
  TeachRequest,
  RelationType,
  EnrichedApproval,
  EnrichedReviewGate,
  CanvasDocument,
  ReviewGate,
  ReviewPolicy,
  Run,
  RunClarification,
  RunEvidence,
  RunStageRecord,
  TaxonomyLabel,
  Tenant,
  TenantMembership,
  Tool,
  Trigger,
  TriggerStatus,
  UpdateReviewPolicy,
  UpdateTaxonomyLabel,
  UpdateTrigger,
  UpdateWorkspace,
  CreateWorkflowType,
  UpdateWorkflowType,
  WorkflowType,
  Workspace,
  WorkspaceOrg,
  WorkspaceProposalRecord,
  WorkspaceStatus,
} from '@orqforge/shared';
import { authHeaders, handleApiStatus } from './http';


function withQuery(
  url: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!params) return url;
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    q.set(key, String(value));
  }
  const s = q.toString();
  return s ? `${url}?${s}` : url;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateMcpServerInput {
  name: string;
  transport: 'http' | 'sse';
  url?: string;
  enabled?: boolean;
}

export interface McpToolInfo {
  name: string;
  description?: string;
}

export interface CreateEvalSetInput {
  agentId: string;
  name: string;
  description?: string;
  passThreshold?: number;
}

export interface AddEvalCaseInput {
  input: string;
  expectedContains: string[];
  rubric?: string;
}

export interface AccessGroupMember {
  userId: string;
  email: string | null;
}

export interface AccessGroup {
  id: string;
  name: string;
  description: string;
  allTenants: boolean;
  system: boolean;
  permissions: Record<string, Record<string, boolean>>;
  tenantIds: string[];
  members: AccessGroupMember[];
}

export interface UpsertAccessGroup {
  name: string;
  description?: string;
  permissions: Record<string, Record<string, boolean>>;
  allTenants?: boolean;
  tenantIds?: string[];
}

export interface AccessMe {
  permissions: Record<string, Record<string, boolean>>;
  allTenants: boolean;
  tenantAccess: string[];
  isPlatformUser: boolean;
  isOrqForgeAdmin: boolean;
  accessSource: 'groups' | 'legacy';
  catalog: Array<{
    id: string;
    label: string;
    section: string;
    scope: 'tenant' | 'platform' | 'both';
    actions: string[];
  }>;
}

export interface UploadRecord {
  id: string;
  tenantId: string;
  runId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadedFileInfo {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
}

export interface RunOutputFile {
  uploadId?: string;
  filename: string;
  mimeType: string;
  sizeBytes?: number;
  storageKey?: string;
  url?: string;
  contentBase64?: string;
}

export interface WorkLogEntry {
  id: string;
  runId: string;
  kind: 'status' | 'stage' | 'delegation' | 'clarification' | 'review' | 'step' | 'file' | 'output' | 'thinking';
  title: string;
  detail: string;
  status?: string;
  agentName?: string;
  occurredAt: string;
}

export interface WorkLog {
  runId: string;
  generatedAt: string;
  entries: WorkLogEntry[];
}

export interface WorkLogDelta {
  runId: string;
  entries: WorkLogEntry[];
}

export interface RunsStreamPayload {
  runs: Run[];
}

export interface RunDetailSnapshot {
  run: Run;
  stages: RunStageRecord[];
  network: AgentNetwork;
  clarifications: RunClarification[];
  files: RunOutputFile[];
  /** Tool approvals for the run family (all statuses). Inline gate cards. */
  approvals?: Approval[];
  /** Outcome review gates for the run family. Inline gate cards. */
  reviewGates?: ReviewGate[];
}

/** One persisted run_steps row, with agent attribution (GET /api/runs/:id/steps). */
export interface RunStepInfo {
  id: string;
  runId: string;
  agentId: string;
  agentName?: string;
  index: number;
  kind: string;
  content: Record<string, unknown>;
  createdAt: string;
}

export interface RunStepsResponse {
  runId: string;
  steps: RunStepInfo[];
}

export interface IdentityProvider {
  alias: string;
  displayName: string;
  providerId: string;
  enabled: boolean;
  config: Record<string, string>;
  secretRef?: string;
  brokerRedirectUri?: string;
}

export type IdpProviderType = 'azure-entra' | 'oidc';

export interface CreateIdentityProviderDto {
  alias: string;
  displayName: string;
  providerType?: IdpProviderType;
  tenantId?: string;
  clientId: string;
  clientSecret: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
}

export class ApiClient {
  private readonly base = '/api';

  // Workspaces
  async listWorkspaces(): Promise<Workspace[]> {
    return this.request<Workspace[]>('GET', `${this.base}/workspaces`, undefined, undefined);
  }
  async createWorkspace(dto: CreateWorkspace): Promise<Workspace> {
    return this.request<Workspace>('POST', `${this.base}/workspaces`, dto, undefined);
  }
  async updateWorkspace(id: string, dto: UpdateWorkspace): Promise<Workspace> {
    return this.request<Workspace>('PATCH', `${this.base}/workspaces/${id}`, dto, undefined);
  }
  async setWorkspaceStatus(id: string, status: WorkspaceStatus): Promise<Workspace> {
    return this.request<Workspace>('POST', `${this.base}/workspaces/${id}/status/${status}`, {}, undefined);
  }

  // Tenants
  async listAllTenants(): Promise<Tenant[]> {
    return this.request<Tenant[]>('GET', `${this.base}/tenants`, undefined, undefined);
  }
  async listMyTenants(): Promise<Tenant[]> {
    return this.request<Tenant[]>('GET', `${this.base}/tenants/mine`, undefined, undefined);
  }
  async getCurrentTenant(): Promise<Tenant> {
    return this.request<Tenant>('GET', `${this.base}/tenants/me`, undefined, undefined);
  }
  async createTenant(dto: { name: string; slug: string; isolationMode?: string }): Promise<Tenant> {
    return this.request<Tenant>('POST', `${this.base}/tenants`, dto, undefined);
  }
  async updateTenant(
    id: string,
    dto: { name?: string; slug?: string; status?: string },
  ): Promise<Tenant> {
    return this.request<Tenant>('PATCH', `${this.base}/tenants/${id}`, dto, undefined);
  }
  async listTenantMembers(tenantId: string): Promise<TenantMembership[]> {
    return this.request<TenantMembership[]>('GET', `${this.base}/tenants/${tenantId}/members`, undefined, undefined);
  }
  async addTenantMember(
    tenantId: string,
    dto: {
      email: string;
      username: string;
      firstName: string;
      lastName: string;
      password: string;
      temporary?: boolean;
      role?: string;
    },
  ): Promise<TenantMembership> {
    return this.request<TenantMembership>('POST', `${this.base}/tenants/${tenantId}/members`, dto, undefined);
  }
  async updateTenantMember(
    tenantId: string,
    userId: string,
    dto: { role: string },
  ): Promise<TenantMembership> {
    return this.request<TenantMembership>('PATCH', `${this.base}/tenants/${tenantId}/members/${userId}`, dto, undefined);
  }
  async removeTenantMember(tenantId: string, userId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/tenants/${tenantId}/members/${userId}`, undefined, undefined);
  }
  async updateTenantMemberProfile(
    tenantId: string,
    userId: string,
    dto: { firstName?: string; lastName?: string; email?: string },
  ): Promise<unknown> {
    return this.request('PATCH', `${this.base}/tenants/${tenantId}/members/${userId}/profile`, dto, undefined);
  }

  // Identity Providers
  async listIdentityProviders(tenantId: string): Promise<IdentityProvider[]> {
    return this.request<IdentityProvider[]>('GET', `${this.base}/tenants/${tenantId}/identity-providers`, undefined, undefined);
  }
  async createIdentityProvider(
    tenantId: string,
    dto: CreateIdentityProviderDto,
  ): Promise<unknown> {
    return this.request('POST', `${this.base}/tenants/${tenantId}/identity-providers`, dto, undefined);
  }
  async updateIdentityProvider(
    tenantId: string,
    alias: string,
    dto: {
      displayName?: string;
      clientId?: string;
      clientSecret?: string;
      authorizationUrl?: string;
      tokenUrl?: string;
      userInfoUrl?: string;
      enabled?: boolean;
    },
  ): Promise<unknown> {
    return this.request('PATCH', `${this.base}/tenants/${tenantId}/identity-providers/${alias}`, dto, undefined);
  }
  async deleteIdentityProvider(tenantId: string, alias: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/tenants/${tenantId}/identity-providers/${alias}`, undefined, undefined);
  }
  async updateIdentityProviderSecrets(
    tenantId: string,
    alias: string,
    values: Record<string, string>,
  ): Promise<unknown> {
    return this.request('POST', `${this.base}/tenants/${tenantId}/identity-providers/${alias}/secrets`, { values }, undefined);
  }

  // Agents
  async listCursorModels(): Promise<{ cursor: { models: import('@orqforge/shared').CursorCatalogModel[] } }> {
    return this.request<{ cursor: { models: import('@orqforge/shared').CursorCatalogModel[] } }>('GET', `${this.base}/cursor/models`, undefined, undefined);
  }
  async listAgents(search = ''): Promise<Paginated<Agent>> {
    return this.request<Paginated<Agent>>('GET', `${this.base}/agents`, undefined, { search });
  }
  async getAgent(id: string): Promise<Agent> {
    return this.request<Agent>('GET', `${this.base}/agents/${id}`, undefined, undefined);
  }
  async createAgent(dto: CreateAgent): Promise<Agent> {
    return this.request<Agent>('POST', `${this.base}/agents`, dto, undefined);
  }
  async updateAgent(id: string, dto: UpdateAgent): Promise<Agent> {
    return this.request<Agent>('PATCH', `${this.base}/agents/${id}`, dto, undefined);
  }
  async syncAgent(id: string): Promise<{
    status: 'in_sync' | 'pending_sync' | 'error';
    lastSyncedAt: string | null;
    lastError: string | null;
  }> {
    return this.request<{
      status: 'in_sync' | 'pending_sync' | 'error';
      lastSyncedAt: string | null;
      lastError: string | null;
    }>('POST', `${this.base}/agents/${id}/sync`, {}, undefined);
  }
  async deleteAgent(id: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>('DELETE', `${this.base}/agents/${id}`, undefined, undefined);
  }
  async agentTools(id: string): Promise<Tool[]> {
    return this.request<Tool[]>('GET', `${this.base}/agents/${id}/tools`, undefined, undefined);
  }
  async assignTool(agentId: string, toolId: string): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${agentId}/tools`, { toolId }, undefined);
  }
  async unassignTool(agentId: string, toolId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${agentId}/tools/${toolId}`, undefined, undefined);
  }
  async agentSkills(id: string): Promise<Skill[]> {
    return this.request<Skill[]>('GET', `${this.base}/agents/${id}/skills`, undefined, undefined);
  }
  async assignSkill(agentId: string, skillId: string): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${agentId}/skills/${skillId}`, {}, undefined);
  }
  async unassignSkill(agentId: string, skillId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${agentId}/skills/${skillId}`, undefined, undefined);
  }
  async agentIntegrations(id: string): Promise<Integration[]> {
    return this.request<Integration[]>('GET', `${this.base}/agents/${id}/integrations`, undefined, undefined);
  }
  async assignIntegration(agentId: string, integrationId: string): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${agentId}/integrations`, { integrationId }, undefined);
  }
  async unassignIntegration(agentId: string, integrationId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${agentId}/integrations/${integrationId}`, undefined, undefined);
  }
  async agentMcpServers(agentId: string): Promise<McpServer[]> {
    return this.request<McpServer[]>('GET', `${this.base}/agents/${agentId}/mcp-servers`, undefined, undefined);
  }
  async assignMcpServer(agentId: string, mcpServerId: string): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${agentId}/mcp-servers`, { mcpServerId }, undefined);
  }
  async unassignMcpServer(agentId: string, mcpServerId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${agentId}/mcp-servers/${mcpServerId}`, undefined, undefined);
  }
  async agentKnowledgeBases(agentId: string): Promise<KnowledgeBase[]> {
    return this.request<KnowledgeBase[]>('GET', `${this.base}/agents/${agentId}/knowledge-bases`, undefined, undefined);
  }
  async assignKnowledgeBase(agentId: string, kbId: string): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${agentId}/knowledge-bases/${kbId}`, {}, undefined);
  }
  async unassignKnowledgeBase(agentId: string, kbId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${agentId}/knowledge-bases/${kbId}`, undefined, undefined);
  }

  // Hierarchy / Org (Epic C)
  async getWorkspaceOrg(workspaceId: string): Promise<WorkspaceOrg> {
    return this.request<WorkspaceOrg>('GET', `${this.base}/workspaces/${workspaceId}/org`, undefined, undefined);
  }
  async addRelation(
    parentAgentId: string,
    childAgentId: string,
    relationType: RelationType,
  ): Promise<unknown> {
    return this.request('POST', `${this.base}/agents/${parentAgentId}/relations`, {
      childAgentId,
      relationType,
    }, undefined);
  }
  async removeRelation(
    parentAgentId: string,
    childAgentId: string,
    relationType: RelationType,
  ): Promise<unknown> {
    return this.request('DELETE', `${this.base}/agents/${parentAgentId}/relations/${childAgentId}/${relationType}`, undefined, undefined);
  }

  // Tools
  async listTools(search = '', pageSize = 25): Promise<Paginated<Tool>> {
    return this.request<Paginated<Tool>>('GET', `${this.base}/tools`, undefined, { search, pageSize });
  }
  async createTool(dto: CreateTool): Promise<Tool> {
    return this.request<Tool>('POST', `${this.base}/tools`, dto, undefined);
  }
  async updateTool(id: string, dto: UpdateTool): Promise<Tool> {
    return this.request<Tool>('PATCH', `${this.base}/tools/${id}`, dto, undefined);
  }
  async updateToolSecrets(id: string, values: Record<string, string>): Promise<Tool> {
    return this.request<Tool>('POST', `${this.base}/tools/${id}/secrets`, { values }, undefined);
  }
  async deleteTool(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/tools/${id}`, undefined, undefined);
  }

  // Integrations
  async listIntegrations(): Promise<Integration[]> {
    return this.request<Integration[]>('GET', `${this.base}/integrations`, undefined, undefined);
  }
  async createIntegration(dto: CreateIntegration): Promise<Integration> {
    return this.request<Integration>('POST', `${this.base}/integrations`, dto, undefined);
  }
  async updateIntegration(id: string, dto: UpdateIntegration): Promise<Integration> {
    return this.request<Integration>('PATCH', `${this.base}/integrations/${id}`, dto, undefined);
  }
  async updateIntegrationSecrets(id: string, values: Record<string, string>): Promise<Integration> {
    return this.request<Integration>('POST', `${this.base}/integrations/${id}/secrets`, { values }, undefined);
  }
  async setIntegrationStatus(id: string, status: IntegrationStatus): Promise<Integration> {
    return this.request<Integration>('PATCH', `${this.base}/integrations/${id}`, { status }, undefined);
  }
  async deleteIntegration(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/integrations/${id}`, undefined, undefined);
  }

  // Skills
  async listSkills(): Promise<Paginated<Skill>> {
    return this.request<Paginated<Skill>>('GET', `${this.base}/skills`, undefined, undefined);
  }
  async createSkill(dto: CreateSkill): Promise<Skill> {
    return this.request<Skill>('POST', `${this.base}/skills`, dto, undefined);
  }
  async updateSkill(id: string, dto: UpdateSkill): Promise<Skill> {
    return this.request<Skill>('PATCH', `${this.base}/skills/${id}`, dto, undefined);
  }
  async deleteSkill(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/skills/${id}`, undefined, undefined);
  }

  // MCP servers
  async listMcpServers(): Promise<McpServer[]> {
    return this.request<McpServer[]>('GET', `${this.base}/mcp-servers`, undefined, undefined);
  }
  async createMcpServer(dto: CreateMcpServerInput): Promise<McpServer> {
    return this.request<McpServer>('POST', `${this.base}/mcp-servers`, dto, undefined);
  }
  async deleteMcpServer(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/mcp-servers/${id}`, undefined, undefined);
  }
  async listMcpTools(id: string): Promise<McpToolInfo[]> {
    return this.request<McpToolInfo[]>('GET', `${this.base}/mcp-servers/${id}/tools`, undefined, undefined);
  }

  // Eval sets
  async listEvalSets(workspaceId?: string): Promise<EvalSet[]> {
    const params: Record<string, string> = {};
    if (workspaceId) params['workspaceId'] = workspaceId;
    return this.request<EvalSet[]>('GET', `${this.base}/eval-sets`, undefined, params);
  }
  async getEvalSet(id: string): Promise<EvalSet> {
    return this.request<EvalSet>('GET', `${this.base}/eval-sets/${id}`, undefined, undefined);
  }
  async createEvalSet(dto: CreateEvalSetInput): Promise<EvalSet> {
    return this.request<EvalSet>('POST', `${this.base}/eval-sets`, dto, undefined);
  }
  async updateEvalSet(id: string, dto: { name?: string; description?: string; passThreshold?: number }): Promise<EvalSet> {
    return this.request<EvalSet>('PATCH', `${this.base}/eval-sets/${id}`, dto, undefined);
  }
  async deleteEvalSet(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/eval-sets/${id}`, undefined, undefined);
  }
  async listEvalCases(setId: string): Promise<EvalCase[]> {
    return this.request<EvalCase[]>('GET', `${this.base}/eval-sets/${setId}/cases`, undefined, undefined);
  }
  async addEvalCase(id: string, dto: AddEvalCaseInput): Promise<EvalCase> {
    return this.request<EvalCase>('POST', `${this.base}/eval-sets/${id}/cases`, dto, undefined);
  }
  async addEvalCasesBulk(id: string, dto: { cases: AddEvalCaseInput[] }): Promise<EvalCase[]> {
    return this.request<EvalCase[]>('POST', `${this.base}/eval-sets/${id}/cases/bulk`, dto, undefined);
  }
  async updateEvalCase(caseId: string, dto: AddEvalCaseInput): Promise<EvalCase> {
    return this.request<EvalCase>('PATCH', `${this.base}/eval-sets/cases/${caseId}`, dto, undefined);
  }
  async deleteEvalCase(caseId: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/eval-sets/cases/${caseId}`, undefined, undefined);
  }
  async listEvalResults(setId: string): Promise<EvalResult[]> {
    return this.request<EvalResult[]>('GET', `${this.base}/eval-sets/${setId}/results`, undefined, undefined);
  }
  async runEvalSet(id: string): Promise<EvalResult> {
    return this.request<EvalResult>('POST', `${this.base}/eval-sets/${id}/run`, {}, undefined);
  }
  async ciEvalTrigger(dto: { agentId: string; evalSetId?: string }): Promise<EvalResult> {
    return this.request<EvalResult>('POST', `${this.base}/eval-sets/ci`, dto, undefined);
  }
  async curateGoldenCandidates(agentId: string): Promise<{ curated: number }> {
    return this.request<{ curated: number }>('POST', `${this.base}/eval-sets/curate/${agentId}`, {}, undefined);
  }

  // Knowledge
  async listKnowledgeBases(): Promise<KnowledgeBase[]> {
    return this.request<KnowledgeBase[]>('GET', `${this.base}/knowledge-bases`, undefined, undefined);
  }
  async createKnowledgeBase(dto: CreateKnowledgeBase): Promise<KnowledgeBase> {
    return this.request<KnowledgeBase>('POST', `${this.base}/knowledge-bases`, dto, undefined);
  }
  async deleteKnowledgeBase(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/knowledge-bases/${id}`, undefined, undefined);
  }
  async listDocuments(kbId: string): Promise<Document[]> {
    return this.request<Document[]>('GET', `${this.base}/knowledge-bases/${kbId}/documents`, undefined, undefined);
  }
  async ingestDocument(kbId: string, dto: IngestDocument): Promise<Document> {
    return this.request<Document>('POST', `${this.base}/knowledge-bases/${kbId}/documents`, dto, undefined);
  }
  async retrieveKnowledge(kbId: string, dto: RetrievalQuery): Promise<RetrievedChunk[]> {
    return this.request<RetrievedChunk[]>('POST', `${this.base}/knowledge-bases/${kbId}/retrieve`, dto, undefined);
  }

  // Uploads
  async uploadFile(file: File, runId?: string): Promise<UploadRecord> {
    const fd = new FormData();
    fd.append('file', file);
    const params: Record<string, string> = {};
    if (runId) params['runId'] = runId;
    return this.requestForm<UploadRecord>(`${this.base}/uploads`, fd, params);
  }
  async getUploadUrl(id: string): Promise<{ url: string }> {
    return this.request<{ url: string }>('GET', `${this.base}/uploads/${id}/url`, undefined, undefined);
  }
  async getUpload(id: string): Promise<Blob> {
    return this.requestBlob(`${this.base}/uploads/${id}`, undefined);
  }

  // Runs
  async listRuns(filters: { conversationId?: string; agentId?: string } = {}): Promise<Run[]> {
    const params: Record<string, string> = {};
    if (filters.conversationId) params['conversationId'] = filters.conversationId;
    if (filters.agentId) params['agentId'] = filters.agentId;
    return this.request<Run[]>('GET', `${this.base}/runs`, undefined, params);
  }
  async getRunSteps(id: string): Promise<RunStepsResponse> {
    return this.request<RunStepsResponse>('GET', `${this.base}/runs/${id}/steps`, undefined, undefined);
  }
  async getRunStages(id: string): Promise<RunStageRecord[]> {
    return this.request<RunStageRecord[]>('GET', `${this.base}/runs/${id}/stages`, undefined, undefined);
  }
  async getRunNetwork(id: string): Promise<AgentNetwork> {
    return this.request<AgentNetwork>('GET', `${this.base}/runs/${id}/network`, undefined, undefined);
  }
  async getRunEvidence(id: string): Promise<RunEvidence> {
    return this.request<RunEvidence>('GET', `${this.base}/runs/${id}/evidence`, undefined, undefined);
  }
  async getRunFiles(id: string): Promise<{ runId: string; files: RunOutputFile[] }> {
    return this.request<{ runId: string; files: RunOutputFile[] }>('GET', `${this.base}/runs/${id}/files`, undefined, undefined);
  }
  async listCanvases(): Promise<
    Array<{
      id: string;
      runId: string | null;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      createdAt: string;
      url: string | null;
      contentBase64?: string;
    }>
  > {
    return this.request<
      Array<{
        id: string;
        runId: string | null;
        filename: string;
        mimeType: string;
        sizeBytes: number;
        createdAt: string;
        url: string | null;
        contentBase64?: string;
      }>
    >('GET', `${this.base}/runs/canvases`, undefined, undefined);
  }
  async executeTool(
    toolKey: string,
    args: Record<string, unknown> = {},
  ): Promise<{ result: unknown; canvas: CanvasDocument | null }> {
    return this.request<{ result: unknown; canvas: CanvasDocument | null }>('POST', `${this.base}/tools/execute`, {
      toolKey,
      arguments: args,
    }, undefined);
  }
  async getRunDetailSnapshot(id: string): Promise<RunDetailSnapshot> {
    return this.request<RunDetailSnapshot>('GET', `${this.base}/runs/${id}/detail`, undefined, undefined);
  }
  async getRunWorkLog(id: string): Promise<WorkLog> {
    return this.request<WorkLog>('GET', `${this.base}/runs/${id}/work-log`, undefined, undefined);
  }
  async resumeRun(id: string, instructions?: string): Promise<Run> {
    return this.request<Run>('POST', `${this.base}/runs/${id}/resume`, { instructions }, undefined);
  }
  async cancelRun(id: string, reason?: string): Promise<Run> {
    return this.request<Run>('POST', `${this.base}/runs/${id}/cancel`, { reason }, undefined);
  }
  async forceCancelRun(id: string, reason?: string): Promise<Run> {
    return this.request<Run>('POST', `${this.base}/runs/${id}/force-cancel`, { reason }, undefined);
  }
  async exportRunEvidencePdf(id: string): Promise<Blob> {
    return this.requestBlob(`${this.base}/runs/${id}/evidence`, { format: 'pdf' });
  }
  async getRunClarifications(id: string): Promise<RunClarification[]> {
    return this.request<RunClarification[]>('GET', `${this.base}/runs/${id}/clarifications`, undefined, undefined);
  }
  async answerClarification(id: string, cid: string, answer: string): Promise<RunClarification> {
    return this.request<RunClarification>('POST', `${this.base}/runs/${id}/clarifications/${cid}/answer`, { answer }, undefined);
  }
  async startRun(agentId: string, input: string): Promise<Run> {
    return this.request<Run>('POST', `${this.base}/runs`, { agentId, input }, undefined);
  }

  // Taxonomy (Epic A2)
  async listTaxonomy(): Promise<TaxonomyLabel[]> {
    return this.request<TaxonomyLabel[]>('GET', `${this.base}/taxonomy`, undefined, undefined);
  }
  async replaceTaxonomy(labels: CreateTaxonomyLabel[]): Promise<TaxonomyLabel[]> {
    return this.request<TaxonomyLabel[]>('PUT', `${this.base}/taxonomy`, labels, undefined);
  }
  async createTaxonomy(dto: CreateTaxonomyLabel): Promise<TaxonomyLabel> {
    return this.request<TaxonomyLabel>('POST', `${this.base}/taxonomy`, dto, undefined);
  }
  async updateTaxonomy(id: string, dto: UpdateTaxonomyLabel): Promise<TaxonomyLabel> {
    return this.request<TaxonomyLabel>('PATCH', `${this.base}/taxonomy/${id}`, dto, undefined);
  }
  async deleteTaxonomy(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/taxonomy/${id}`, undefined, undefined);
  }

  // Workflow types (Epic K)
  async listWorkflowTypes(): Promise<WorkflowType[]> {
    return this.request<WorkflowType[]>('GET', `${this.base}/workflow-types`, undefined, undefined);
  }
  async createWorkflowType(dto: CreateWorkflowType): Promise<WorkflowType> {
    return this.request<WorkflowType>('POST', `${this.base}/workflow-types`, dto, undefined);
  }
  async updateWorkflowType(id: string, dto: UpdateWorkflowType): Promise<WorkflowType> {
    return this.request<WorkflowType>('PATCH', `${this.base}/workflow-types/${id}`, dto, undefined);
  }
  async deleteWorkflowType(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/workflow-types/${id}`, undefined, undefined);
  }

  // Triggers (Epic D)
  async listTriggers(): Promise<Trigger[]> {
    return this.request<Trigger[]>('GET', `${this.base}/triggers`, undefined, undefined);
  }
  async createTrigger(dto: CreateTrigger): Promise<Trigger> {
    return this.request<Trigger>('POST', `${this.base}/triggers`, dto, undefined);
  }
  async updateTrigger(id: string, dto: UpdateTrigger): Promise<Trigger> {
    return this.request<Trigger>('PATCH', `${this.base}/triggers/${id}`, dto, undefined);
  }
  async setTriggerStatus(id: string, status: TriggerStatus): Promise<Trigger> {
    return this.request<Trigger>('POST', `${this.base}/triggers/${id}/status/${status}`, {}, undefined);
  }
  async updateTriggerSecrets(id: string, values: Record<string, string>): Promise<Trigger> {
    return this.request<Trigger>('POST', `${this.base}/triggers/${id}/secrets`, { values }, undefined);
  }
  async deleteTrigger(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/triggers/${id}`, undefined, undefined);
  }
  async sendIntakeTest(
    triggerId: string,
    text: string,
  ): Promise<{ runId: string; agentId: string; label?: string; matched: boolean }> {
    return this.request<{ runId: string; agentId: string; label?: string; matched: boolean }>('POST', `${this.base}/intake/${triggerId}`, { text }, undefined);
  }

  // Review gates + policies (Epics H, A3)
  async listReviewGates(status?: 'pending' | 'decided' | 'all'): Promise<EnrichedReviewGate[]> {
    return this.request<EnrichedReviewGate[]>('GET', `${this.base}/review/gates`, undefined, status ? { status } : {});
  }
  async decideReview(id: string, approve: boolean, reason?: string): Promise<ReviewGate> {
    return this.request<ReviewGate>('POST', `${this.base}/review/gates/${id}/decision`, {
      approve,
      reason,
    }, undefined);
  }
  async listReviewPolicies(): Promise<ReviewPolicy[]> {
    return this.request<ReviewPolicy[]>('GET', `${this.base}/review/policies`, undefined, undefined);
  }
  async createReviewPolicy(dto: CreateReviewPolicy): Promise<ReviewPolicy> {
    return this.request<ReviewPolicy>('POST', `${this.base}/review/policies`, dto, undefined);
  }
  async updateReviewPolicy(id: string, dto: UpdateReviewPolicy): Promise<ReviewPolicy> {
    return this.request<ReviewPolicy>('PATCH', `${this.base}/review/policies/${id}`, dto, undefined);
  }
  async deleteReviewPolicy(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/review/policies/${id}`, undefined, undefined);
  }

  // AI Trainer / Flow builder (Epic B)
  async listProposals(): Promise<WorkspaceProposalRecord[]> {
    return this.request<WorkspaceProposalRecord[]>('GET', `${this.base}/builder/proposals`, undefined, undefined);
  }
  async propose(prompt: string): Promise<WorkspaceProposalRecord> {
    return this.request<WorkspaceProposalRecord>('POST', `${this.base}/builder/propose`, { prompt }, undefined);
  }
  async applyProposal(id: string): Promise<ApplyProposalResult> {
    return this.request<ApplyProposalResult>('POST', `${this.base}/builder/proposals/${id}/apply`, {}, undefined);
  }
  async discardProposal(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/builder/proposals/${id}`, undefined, undefined);
  }

  // Approvals
  async listApprovals(): Promise<EnrichedApproval[]> {
    return this.request<EnrichedApproval[]>('GET', `${this.base}/approvals/pending`, undefined, undefined);
  }
  async listApprovalsPage(params: Record<string, string> = {}): Promise<{
    items: EnrichedApproval[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    return this.request<{ items: EnrichedApproval[]; total: number; page: number; pageSize: number }>('GET', `${this.base}/approvals`, undefined, params);
  }
  async decideApproval(id: string, approve: boolean, reason?: string): Promise<unknown> {
    return this.request('POST', `${this.base}/approvals/${id}/decision`, { approve, reason }, undefined);
  }

  // Settings
  async getSettings(): Promise<TenantSettings> {
    return this.request<TenantSettings>('GET', `${this.base}/settings`, undefined, undefined);
  }
  async updateSettings(dto: UpdateTenantSettings): Promise<TenantSettings> {
    return this.request<TenantSettings>('PUT', `${this.base}/settings`, dto, undefined);
  }

  // Admin settings (global, orqforge_admin only)
  async listModelPricing(): Promise<ModelPricing[]> {
    return this.request<ModelPricing[]>('GET', `${this.base}/admin/settings/pricing`, undefined, undefined);
  }
  async createModelPricing(dto: CreateModelPricing): Promise<ModelPricing> {
    return this.request<ModelPricing>('POST', `${this.base}/admin/settings/pricing`, dto, undefined);
  }
  async updateModelPricing(id: string, dto: UpdateModelPricing): Promise<ModelPricing> {
    return this.request<ModelPricing>('PUT', `${this.base}/admin/settings/pricing/${id}`, dto, undefined);
  }
  async deleteModelPricing(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/admin/settings/pricing/${id}`, undefined, undefined);
  }

  async listPlatformIdentityProviders(): Promise<IdentityProvider[]> {
    return this.request<IdentityProvider[]>('GET', `${this.base}/admin/identity-providers`, undefined, undefined);
  }
  async createPlatformIdentityProvider(dto: CreateIdentityProviderDto): Promise<unknown> {
    return this.request('POST', `${this.base}/admin/identity-providers`, dto, undefined);
  }
  async updatePlatformIdentityProvider(
    alias: string,
    dto: { displayName?: string; enabled?: boolean; tenantId?: string; clientId?: string },
  ): Promise<unknown> {
    return this.request('PATCH', `${this.base}/admin/identity-providers/${alias}`, dto, undefined);
  }
  async deletePlatformIdentityProvider(alias: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/admin/identity-providers/${alias}`, undefined, undefined);
  }

  async getAccessMe(): Promise<AccessMe> {
    return this.request<AccessMe>('GET', `${this.base}/access/me`, undefined, undefined);
  }
  async listGroups(): Promise<AccessGroup[]> {
    return this.request<AccessGroup[]>('GET', `${this.base}/groups`, undefined, undefined);
  }
  async createGroup(dto: UpsertAccessGroup): Promise<AccessGroup> {
    return this.request<AccessGroup>('POST', `${this.base}/groups`, dto, undefined);
  }
  async updateGroup(id: string, dto: Partial<UpsertAccessGroup>): Promise<AccessGroup> {
    return this.request<AccessGroup>('PATCH', `${this.base}/groups/${id}`, dto, undefined);
  }
  async deleteGroup(id: string): Promise<unknown> {
    return this.request('DELETE', `${this.base}/groups/${id}`, undefined, undefined);
  }
  async addGroupMember(id: string, dto: { userId: string; email?: string }): Promise<AccessGroup> {
    return this.request<AccessGroup>('POST', `${this.base}/groups/${id}/members`, dto, undefined);
  }
  async removeGroupMember(id: string, userId: string): Promise<AccessGroup> {
    return this.request<AccessGroup>('DELETE', `${this.base}/groups/${id}/members/${userId}`, undefined, undefined);
  }

  // Upload from URL
  async uploadFromUrl(url: string): Promise<UploadRecord> {
    return this.request<UploadRecord>('POST', `${this.base}/uploads/from-url`, { url }, undefined);
  }

  // Chat
  async chat(
    agentId: string,
    message: string,
    conversationId?: string,
    uploadIds?: string[],
  ): Promise<{ conversationId: string; output: string }> {
    return this.request<{ conversationId: string; output: string }>('POST', `${this.base}/chat`, {
      agentId,
      message,
      conversationId,
      uploadIds,
    }, undefined);
  }
  async listConversations(agentId?: string): Promise<Conversation[]> {
    const params: Record<string, string> = agentId ? { agentId } : {};
    return this.request<Conversation[]>('GET', `${this.base}/chat/conversations`, undefined, params);
  }
  async getMessages(conversationId: string): Promise<Message[]> {
    return this.request<Message[]>('GET', `${this.base}/chat/conversations/${conversationId}/messages`, undefined, undefined);
  }
  async runFromChat(
    agentId: string,
    message: string,
    conversationId?: string,
    uploadIds?: string[],
  ): Promise<{ conversationId: string; runId: string; status: string }> {
    return this.request<{ conversationId: string; runId: string; status: string }>('POST', `${this.base}/chat/run`, { agentId, message, conversationId, uploadIds }, undefined);
  }

  /** Unified Collaborate send: start a run or queue into the active session. */
  async sendChatMessage(
    agentId: string,
    message: string,
    conversationId?: string,
    uploadIds?: string[],
  ): Promise<{
    conversationId: string;
    messageId: string;
    runId?: string;
    status: 'started' | 'queued' | 'applied';
  }> {
    return this.request<{
      conversationId: string;
      messageId: string;
      runId?: string;
      status: 'started' | 'queued' | 'applied';
    }>('POST', `${this.base}/chat/message`, { agentId, message, conversationId, uploadIds }, undefined);
  }

  // Governed learning (Epic I4)
  async listLearning(status?: string): Promise<LearningSuggestion[]> {
    const params: Record<string, string> = status ? { status } : {};
    return this.request<LearningSuggestion[]>('GET', `${this.base}/learning/suggestions`, undefined, params);
  }
  async decideLearning(id: string, accept: boolean, reason?: string): Promise<LearningSuggestion> {
    return this.request<LearningSuggestion>('POST', `${this.base}/learning/suggestions/${id}/decision`, { accept, reason }, undefined);
  }
  // Evolution (Epic M)
  async applyLearning(id: string): Promise<ApplyResult> {
    return this.request<ApplyResult>('POST', `${this.base}/learning/suggestions/${id}/apply`, {}, undefined);
  }
  async teach(body: TeachRequest): Promise<LearningSuggestion> {
    return this.request<LearningSuggestion>('POST', `${this.base}/learning/teach`, body, undefined);
  }
  async listInsights(): Promise<EvolutionInsight[]> {
    return this.request<EvolutionInsight[]>('GET', `${this.base}/learning/insights`, undefined, undefined);
  }
  async computeInsights(): Promise<EvolutionInsight[]> {
    return this.request<EvolutionInsight[]>('POST', `${this.base}/learning/insights/compute`, {}, undefined);
  }

  // Cost analytics (orqforge_admin only)
  async getCostOverview(): Promise<CostOverview> {
    return this.request<CostOverview>('GET', `${this.base}/costs/overview`, undefined, undefined);
  }
  async getCostSummary(filters: Record<string, string> = {}): Promise<CostSummaryItem[]> {
    return this.request<CostSummaryItem[]>('GET', `${this.base}/costs/summary`, undefined, filters);
  }
  async getCostTimeseries(params: Record<string, string>): Promise<TimeseriesPoint[]> {
    return this.request<TimeseriesPoint[]>('GET', `${this.base}/costs/timeseries`, undefined, params);
  }
  async getCostRuns(filters: Record<string, string> = {}): Promise<PaginatedRunCosts> {
    return this.request<PaginatedRunCosts>('GET', `${this.base}/costs/runs`, undefined, filters);
  }
  async getCostRunDetail(runId: string): Promise<RunCostDetail> {
    return this.request<RunCostDetail>('GET', `${this.base}/costs/runs/${runId}`, undefined, undefined);
  }

  async getApDemoOverview(): Promise<ApDemoOverview> {
    return this.request<ApDemoOverview>('GET', `${this.base}/ap-demo/overview`, undefined, undefined);
  }
  async enableApDemo(): Promise<{ packKey: string; toolKeys: string[] }> {
    return this.request<{ packKey: string; toolKeys: string[] }>('POST', `${this.base}/ap-demo/enable`, {}, undefined);
  }
  async receiveApInvoice(scenarioId: string): Promise<{ case: ApDemoCase; inbound: ApDemoMessage; hitl: ApDemoMessage | null }> {
    return this.request<{ case: ApDemoCase; inbound: ApDemoMessage; hitl: ApDemoMessage | null }>('POST', `${this.base}/ap-demo/receive-invoice`, { scenario_id: scenarioId }, undefined);
  }
  async replyApMail(
    messageId: string,
    body: string,
    decision: 'APPROVE' | 'REJECT' = 'APPROVE',
  ): Promise<{ case: ApDemoCase; reply: ApDemoMessage }> {
    return this.request<{ case: ApDemoCase; reply: ApDemoMessage }>('POST', `${this.base}/ap-demo/reply/${messageId}`, { body, decision }, undefined);
  }
  async payApRun(): Promise<{ approved: number; bankExecution: boolean; overview: ApDemoOverview }> {
    return this.request<{ approved: number; bankExecution: boolean; overview: ApDemoOverview }>('POST', `${this.base}/ap-demo/pay`, {}, undefined);
  }
  async resetApDemo(): Promise<{ reset: boolean }> {
    return this.request<{ reset: boolean }>('POST', `${this.base}/ap-demo/reset`, {}, undefined);
  }

  async request<T>(
    method: string,
    url: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined | null>,
  ): Promise<T> {
    const headers = await authHeaders();
    const path = withQuery(url, params);
    const init: RequestInit = { method, headers };
    if (body !== undefined && method !== 'GET' && method !== 'HEAD') {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const res = await fetch(path, init);
    await handleApiStatus(res);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async requestBlob(
    url: string,
    params?: Record<string, string | number | boolean | undefined | null>,
  ): Promise<Blob> {
    const headers = await authHeaders();
    const res = await fetch(withQuery(url, params), { headers });
    await handleApiStatus(res);
    return res.blob();
  }

  async requestForm<T>(
    url: string,
    fd: FormData,
    params?: Record<string, string | number | boolean | undefined | null>,
  ): Promise<T> {
    const headers = await authHeaders();
    const res = await fetch(withQuery(url, params), { method: 'POST', headers, body: fd });
    await handleApiStatus(res);
    return (await res.json()) as T;
  }
}

export interface CostOverview {
  todayCostCents: number;
  mtdCostCents: number;
  allTimeCostCents: number;
  totalElapsedMs: number;
  topTenant: { tenantId: string; tenantName: string; costCents: number } | null;
  activeAgentCount: number;
  runCountToday: number;
  usageRowCount: number;
  pricing: { source: string; fetchedAt: string | null; modelCount: number };
}

export interface CostSummaryItem {
  tenantId: string;
  tenantName: string;
  workspaceId: string | null;
  workspaceName: string | null;
  agentId: string | null;
  agentName: string | null;
  costCents: number;
  tokensIn: number;
  tokensOut: number;
  totalElapsedMs: number;
  runCount: number;
}

export interface TimeseriesPoint {
  period: string;
  costCents: number;
  tokensIn: number;
  tokensOut: number;
  totalElapsedMs: number;
  runCount: number;
}

export interface RunCostItem {
  runId: string;
  agentId: string;
  agentName: string;
  workspaceId: string | null;
  workspaceName: string | null;
  tenantId: string;
  tenantName: string;
  costCents: number;
  tokensIn: number;
  tokensOut: number;
  totalElapsedMs: number;
  runStatus: string;
  runCreatedAt: string;
  agentModel?: string | null;
}

export interface PaginatedRunCosts {
  items: RunCostItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ApDemoProcessStep {
  key: string;
  label: string;
  actor: string;
  human: boolean;
}

export interface ApDemoScenario {
  scenario_id: string;
  case_id: string;
  attachment_label: string;
  supplier_name: string;
  gross_total: number;
  currency: string;
  expected_status: string;
  expected_reason_codes: string[];
  expected_approver_role: string | null;
  notes: string;
}

export interface ApDemoCase {
  case_id: string;
  scenario_id: string;
  status: string;
  phase: string;
  phase_label: string;
  vendor_id: string | null;
  vendor_name: string;
  iban: string;
  company_code: string | null;
  po_number: string | null;
  invoice_number: string;
  currency: string;
  gross_total: number;
  reason_codes: string[];
  explanation: string;
  approver_role: string | null;
  payment_line_id: string | null;
  updated_at: string;
}

export interface ApDemoPayment {
  payment_line_id: string;
  case_id: string;
  vendor_id: string;
  vendor_name: string;
  company_code: string;
  iban: string;
  amount: number;
  currency: string;
  due_date: string;
  invoice_number: string;
}

export interface ApDemoAudit {
  timestamp: string;
  step: string;
  actor_id: string;
  action: string;
  decision: string;
  case_id: string;
  reason_codes: string[];
}

export interface ApDemoMessage {
  id: string;
  folder: string;
  direction: string;
  scenarioId: string | null;
  caseId: string | null;
  fromAddress: string;
  toAddress: string;
  ccAddress: string | null;
  subject: string;
  body: string;
  attachmentLabel: string | null;
  status: string;
  createdAt: string;
}

export interface ApDemoOverview {
  illustrative: boolean;
  packEnabled: boolean;
  processSteps: ApDemoProcessStep[];
  scenarios: ApDemoScenario[];
  mailboxAddress: string;
  cases: ApDemoCase[];
  payments: ApDemoPayment[];
  audit: ApDemoAudit[];
  mailbox: ApDemoMessage[];
}

export interface RunCostDetail extends RunCostItem {
  steps: {
    id: string;
    kind: string;
    tokensIn: number;
    tokensOut: number;
    costUsdCents: number;
    index: number;
  }[];
  stages: {
    stage: string;
    status: string;
    elapsedMs: number;
    tokens: number;
  }[];
}

export const apiClient = new ApiClient();
